// Page templates: whole pages built from MDS components, to start a site from.
// The React source lives in src/templates; this is what the site, search and
// the machine-readable /mds/templates.json say about each one.

export type PageTemplate = {
  id: 'agency-home' | 'service-landing' | 'multi-step-form' | 'search-results'
  name: string
  summary: string
  useWhen: string[]
  anatomy: string[]
  accessibility: string[]
  components: string[]
  /** The React source file under src/templates. */
  source: string
}

export const pageTemplates: PageTemplate[] = [
  {
    id: 'agency-home',
    name: 'Agency home',
    summary: 'An agency’s front door: who it is, what it does, the main sections of its site, and its latest news.',
    useWhen: ['The home page of an agency or program site.', 'A landing page that introduces an organization and routes people into its site.'],
    anatomy: ['Official banner', 'Agency header', 'Hero with photograph, name, mission and search', 'Photograph credit', 'Sections as link cards', 'News', 'Footer', 'Agency identifier'],
    accessibility: [
      'A skip link leads to the main content, which holds the page’s one h1: the agency’s name.',
      'The hero photograph is decorative, darkened behind the text, and credited with a link to its source.',
      'Each card is one link named by its title, so lists of links read cleanly.',
    ],
    components: ['banner', 'header', 'search', 'card', 'footer', 'identifier'],
    source: 'AgencyHome.tsx',
  },
  {
    id: 'service-landing',
    name: 'Service landing',
    summary: 'One service, start to finish: who it is for, the steps, and a clear way to begin.',
    useWhen: ['A page for one thing people come to do, such as renewing a permit.', 'The page a top task links to, before the form begins.'],
    anatomy: ['Side navigation', 'Breadcrumb', 'Title and lead', 'Summary box', 'Process list', 'Start button', 'Questions in an accordion', 'Contact'],
    accessibility: [
      'The breadcrumb and side navigation are named landmarks, with the current page marked by aria-current.',
      'The summary box has a real heading, so it appears in the headings list.',
      'Questions use native details and summary, which work by keyboard with no script.',
    ],
    components: ['side-navigation', 'breadcrumb', 'summary-box', 'process-list', 'button', 'accordion'],
    source: 'ServiceLanding.tsx',
  },
  {
    id: 'multi-step-form',
    name: 'Multi-step form',
    summary: 'A form split into short steps, with errors named in place and a review before submitting.',
    useWhen: ['Applications and requests with more than a handful of questions.', 'Any form where people need to check their answers before sending.'],
    anatomy: ['Breadcrumb', 'Step indicator', 'Step heading', 'Error summary', 'Fields with hints and errors', 'Review list', 'Back and Continue', 'Confirmation'],
    accessibility: [
      'Each step’s heading takes focus when the step changes, so screen readers hear where they are.',
      'Errors are listed at the top as links to their fields, and repeated beside each field through aria-describedby.',
      'Date of birth uses three labeled fields with autocomplete, not a date picker.',
    ],
    components: ['step-indicator', 'checkbox-radio', 'text-input', 'memorable-date', 'select', 'alert', 'button'],
    source: 'MultiStepForm.tsx',
  },
  {
    id: 'search-results',
    name: 'Search results',
    summary: 'Results for a site search: the query, a count, filters, and results that say where each leads.',
    useWhen: ['The results page for a site search.', 'Any long list people narrow by type and page through.'],
    anatomy: ['Search form with the query', 'Result count', 'Filters', 'Results with type, title link, address and summary', 'Pagination'],
    accessibility: [
      'The count is a status message, so a new search is announced.',
      'Each result’s title is its link; the address beneath it says where it leads.',
      'Pagination is a named navigation landmark with the current page marked.',
    ],
    components: ['search', 'checkbox-radio', 'tag', 'pagination'],
    source: 'SearchResults.tsx',
  },
]

export const findTemplate = (id: string) => pageTemplates.find((template) => template.id === id)
